// Custom Claims Management Utilities
import { httpsCallable } from 'firebase/functions';
import { auth, functions } from './firebase';

/**
 * Initialize admin claims for the current user
 * (One-time setup for admin accounts)
 */
export const initializeAdminClaims = async () => {
  try {
    const initializeAdmin = httpsCallable(functions, 'initializeAdminClaims');
    const result = await initializeAdmin();
    console.log('Admin claims initialized:', result.data);
    
    // Note: Claims will be updated automatically via onIdTokenChanged listener
    
    return result.data;
  } catch (error) {
    console.error('Error initializing admin claims:', error);
    throw error;
  }
};

/**
 * Set custom claims for a user (Admin only)
 */
export const setUserClaims = async (uid, claims) => {
  try {
    const setClaimsFunction = httpsCallable(functions, 'setUserClaims');
    const result = await setClaimsFunction({ uid, claims });
    console.log('Custom claims set:', result.data);
    return result.data;
  } catch (error) {
    console.error('Error setting custom claims:', error);
    throw error;
  }
};

/**
 * Get user's current custom claims (Admin only)
 */
export const getUserClaims = async (uid) => {
  try {
    const getClaimsFunction = httpsCallable(functions, 'getUserClaims');
    const result = await getClaimsFunction({ uid });
    console.log('User claims retrieved:', result.data);
    return result.data;
  } catch (error) {
    console.error('Error getting custom claims:', error);
    throw error;
  }
};

/**
 * Make a user an admin (Admin only)
 */
export const makeUserAdmin = async (uid) => {
  try {
    const makeAdminFunction = httpsCallable(functions, 'makeUserAdmin');
    const result = await makeAdminFunction({ uid });
    console.log('User made admin:', result.data);
    return result.data;
  } catch (error) {
    console.error('Error making user admin:', error);
    throw error;
  }
};

/**
 * Set creator subscription status (Admin only)
 */
export const setCreatorSubscription = async (uid, active, currentPeriodEnd = null) => {
  try {
    const setSubscriptionFunction = httpsCallable(functions, 'setCreatorSubscription');
    const result = await setSubscriptionFunction({ uid, active, currentPeriodEnd });
    console.log('Creator subscription updated:', result.data);
    return result.data;
  } catch (error) {
    console.error('Error setting creator subscription:', error);
    throw error;
  }
};

/**
 * Get current user's custom claims from their ID token
 */
export const getCurrentUserClaims = async () => {
  try {
    if (!auth.currentUser) {
      throw new Error('No authenticated user');
    }
    
    // Use direct token call to get fresh claims
    const idTokenResult = await auth.currentUser.getIdTokenResult(false);
    return idTokenResult.claims;
  } catch (error) {
    console.error('Error getting current user claims:', error);
    throw error;
  }
};

/**
 * Check if current user has a specific claim
 */
export const hasCustomClaim = async (claimName) => {
  try {
    const claims = await getCurrentUserClaims();
    return !!claims[claimName];
  } catch (error) {
    console.error('Error checking custom claim:', error);
    return false;
  }
};

/**
 * Helper to refresh user's ID token and get updated claims
 */
export const refreshUserToken = async () => {
  try {
    if (!auth.currentUser) {
      throw new Error('No authenticated user');
    }
    
    // Note: Claims will be updated automatically via onIdTokenChanged listener
    return await getCurrentUserClaims();
  } catch (error) {
    console.error('Error refreshing user token:', error);
    throw error;
  }
};
